import React, { Fragment, useEffect, useState } from "react";
//import { Link as RouterLink } from 'react-router-dom'
import clsx from "clsx";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@material-ui/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Drawer, Paper, Typography } from "@material-ui/core";
import { Hidden } from "@material-ui/core";
import Navigation from "../navigation";
import navigationConfig from "./navigation-config";
// import { useContext } from 'react'
// import { __RouterContext } from 'react-router'
import { isStaffUser } from "utils";
import { SelectOrganisationForm } from "features/organisations";
import {Snackbar} from "@material-ui/core";
import { PopupSnackbar } from "frame/components";
import { Alert } from "@material-ui/lab";
import ContactSection from "../contact-section";
import PaymentIcon from "@material-ui/icons/Payment";
import AccountIcon from "@material-ui/icons/AccountCircle";
import LockIcon from "@material-ui/icons/Lock";
import {
	ACCOUNT_SUBSCRIPTION_ROUTE,
	ACCOUNT_PROFILE_ROUTE,
	ACCOUNT_PASSWORD_ROUTE,
	ACCOUNT_ROUTE,
} from "features/account/actions/types";

// const useRouter = () => useContext(__RouterContext)

const useStyles = makeStyles((theme) => ({
	root: {
		height: "100vh",
		// Desktop/sidebar width controlled by parent `className` (e.g. 290px)
		overflowY: "auto",
		overflowX: "hidden",
		"&::-webkit-scrollbar": {
			width: "6px",
		},
		"&::-webkit-scrollbar-track": {
			background: "rgba(0, 0, 0, 0.05)",
			borderRadius: "3px",
		},
		"&::-webkit-scrollbar-thumb": {
			background: "rgba(0, 0, 0, 0.2)",
			borderRadius: "3px",
			transition: "all 0.2s ease",
		},
		"&::-webkit-scrollbar-thumb:hover": {
			background: "rgba(0, 0, 0, 0.4)",
		},
		"&::-webkit-scrollbar-thumb:active": {
			background: "rgba(0, 0, 0, 0.6)",
		},
		scrollbarWidth: "thin",
		scrollbarColor: "rgba(0, 0, 0, 0.4) rgba(0, 0, 0, 0.1)",
	},
	fullScreenOverlay: {
		position: "fixed",
		top: 56,
		left: 0,
		right: 0,
		bottom: 0,
		width: "100vw",
		height: "calc(100vh - 56px)",
		zIndex: 3,
		backgroundColor: "rgba(0, 0, 0, 0.5)",
		display: "flex",
	},
	mobileRoot: {
		height: "100vh",
		width: "100vw !important",
		maxWidth: "100vw !important",
		overflowY: "auto",
		overflowX: "hidden",
		"&::-webkit-scrollbar": {
			width: "6px",
		},
		"&::-webkit-scrollbar-track": {
			background: "rgba(0, 0, 0, 0.05)",
			borderRadius: "3px",
		},
		"&::-webkit-scrollbar-thumb": {
			background: "rgba(0, 0, 0, 0.2)",
			borderRadius: "3px",
			transition: "all 0.2s ease",
		},
		"&::-webkit-scrollbar-thumb:hover": {
			background: "rgba(0, 0, 0, 0.4)",
		},
		"&::-webkit-scrollbar-thumb:active": {
			background: "rgba(0, 0, 0, 0.6)",
		},
		scrollbarWidth: "thin",
		scrollbarColor: "rgba(0, 0, 0, 0.4) rgba(0, 0, 0, 0.1)",
	},
	content: {
		padding: theme.spacing(2),
		paddingBottom: theme.spacing(6),
		backgroundColor: theme.palette.background.default, // Use theme background color (#F4F6F8)
		minHeight: "100vh",
		display: "flex",
		flexDirection: "column",
		// Remove background image to match other pages
	},
	profile: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		minHeight: "fit-content",
	},
	avatar: {
		width: 60,
		height: 60,
	},
	name: {
		marginTop: theme.spacing(1),
	},
	divider: {
		marginTop: theme.spacing(2),
	},
	navigation: {
		marginTop: theme.spacing(2),
		flex: 1,
		display: "flex",
		flexDirection: "column",
	},
	organisationSelect: {
		margin: theme.spacing(2),
		color: theme.palette.text.primary, // Use theme text color instead of white
	},
	selectBorder: {
		// borderBottom: 'solid 1px white',
		borderBottom: "none",
	},
	selectIcon: {
		fill: theme.palette.text.primary, // Use theme text color instead of white
	},
	closeButton: {
		position: "absolute",
		top: theme.spacing(2),
		right: theme.spacing(2),
		color: theme.palette.text.primary, // Use theme text color instead of white
		zIndex: 1,
		"&:hover": {
			backgroundColor: theme.palette.action.hover, // Use theme hover color
		},
	},
}));

const useStylesSnackbar = makeStyles((theme) => ({
	root: {},
	content: {
		maxWidth: "280px",
	},
}));

const NavBar = ({
	openMobile,
	onToggleSideNavBar,
	className,
	role,
	currentOrganisationGUID,
	globalMessage,
	profile,
    timesheetToggle,
    hasPendingQueue,
    hasNetwork,
	featureFlags,
	subscriptionStatus,
}) => {
	const theme = useTheme();
	const classes = useStyles();
	const classesSnackbar = useStylesSnackbar();
	const mobileView = useMediaQuery(theme.breakpoints.down("xs"));

	// Track role changes to force Navigation remount
	const [navigationKey, setNavigationKey] = useState(0);
	
	useEffect(() => {
		// Force Navigation components to remount when role changes
		setNavigationKey(prev => prev + 1);
	}, [role]);

	// Build navigation config with custom terminology labels
	const customerLabel =
		featureFlags?.customerLabel && featureFlags.customerLabel.trim()
			? featureFlags.customerLabel.trim()
			: "Customer";
	const siteLabel =
		featureFlags?.siteLabel && featureFlags.siteLabel.trim()
			? featureFlags.siteLabel.trim()
			: "Site";

	const isDisabled = subscriptionStatus === "Disabled";

	// When subscription is disabled, only show the Subscription nav item
	const filteredNavigationConfig = isDisabled
		? [{
			title: "Navigation",
			pages: [{
				title: "Admin",
				href: ACCOUNT_ROUTE,
				children: [
					{ title: "Subscription", href: ACCOUNT_SUBSCRIPTION_ROUTE, icon: PaymentIcon },
					{ title: "Profile", href: ACCOUNT_PROFILE_ROUTE, icon: AccountIcon },
					{ title: "Change Password", href: ACCOUNT_PASSWORD_ROUTE, icon: LockIcon },
				],
			}],
		}]
		: navigationConfig(customerLabel, siteLabel, role);

	const navbarContent = (
		<div className={classes.content}>

			<div className={classes.profile}>
				{/* <Avatar
          alt="Person"
          className={classes.avatar}
          component={RouterLink}
          // src={session.user.avatar}
          to="/profile"
        /> */}
				{/* <Typography className={classes.name} variant="h4">
					{/* {session.user.first_name} {session.user.last_name} */}
				{/* </Typography> */}
				{/* <Typography variant="body2">{session.user.bio}</Typography> */}
			</div>
			{/* <Divider className={classes.divider} /> */}

			<nav className={classes.navigation} key={`nav-${role || "User"}-${navigationKey}`}>
				<SelectOrganisationForm profile={profile} />
			{filteredNavigationConfig.map((list, index) => (
				<Navigation
					component="div"
					key={`${index}-${role || "User"}-${navigationKey}`}
					pages={list.pages}
					title={list.title}
					accountType={role || "User"}
					featureFlags={featureFlags}
					role={role}
					supervisingStaffList={profile && profile.supervisingStaffList}
					currentOrganisation={currentOrganisationGUID}
					organisations={[]} // TODO: Add organisations prop if available
					timesheetToggle={timesheetToggle}
					hasNetwork={hasNetwork}
					openAdminByDefault={isDisabled}
				/>
			))}
				<ContactSection />
			</nav>
		</div>
	);

	const [message, setMessage] = useState([])
	useEffect(() => {
		let globalMessageList = [];
		for(let i =0; i < globalMessage.length; i++){
			globalMessageList.push(globalMessage[i][1]);
		}
		setMessage(globalMessageList);
	}, [globalMessage]);

	// Build banner text: show a single summarised line if multiple messages (no refresh warning needed)
	const bannerText = message.length > 1
		? `Syncing ${message.length} records...`
		: (message[0] || "");

	return (
		<Fragment>
			{message.length > 0 && hasPendingQueue && (
				<Snackbar
					open={true}
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					key={"message"}
				>
					<Alert variant="filled" severity={"success"}>
						<p className={classesSnackbar.content}>{bannerText}</p>
					</Alert>
				</Snackbar>
			)}

			<Hidden smUp>
				{openMobile && (
					<div 
						className={classes.fullScreenOverlay}
						onClick={onToggleSideNavBar}
					>
						<div 
							className={classes.mobileRoot}
							onClick={(e) => e.stopPropagation()}
						>
							{navbarContent}
						</div>
					</div>
				)}
			</Hidden>
			<Hidden xsDown>
				<Paper
					className={clsx(classes.root, className)}
					elevation={1}
					square
				>
					{navbarContent}
				</Paper>
			</Hidden>
		</Fragment>
	);
};

NavBar.propTypes = {
	className: PropTypes.string.isRequired,
	onToggleSideNavBar: PropTypes.func,
	openMobile: PropTypes.bool,
	role: PropTypes.string,
	profile: PropTypes.object,
    hasPendingQueue: PropTypes.bool,
    hasNetwork: PropTypes.bool,
	subscriptionStatus: PropTypes.string,
};

export default NavBar;